import { createContext, useCallback, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('skipya_token'));
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem('skipya_token');
    setToken(null);
    setUser(null);
    setIsAdmin(false);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      
      // Check admin status
      try {
        const adminCheck = await axios.get(`${API}/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(adminCheck.data.is_admin);
      } catch {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('skipya_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Check admin status after login
    try {
      const adminCheck = await axios.get(`${API}/admin/check`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setIsAdmin(adminCheck.data.is_admin);
    } catch {
      setIsAdmin(false);
    }
    
    return userData;
  };

  const register = async (email, password, firstName, lastName = '') => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      first_name: firstName,
      last_name: lastName
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('skipya_token', access_token);
    setToken(access_token);
    setUser(userData);
    setIsAdmin(false); // New users are not admin
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
